import { useEffect, useState } from 'react'
import {
  api,
  type ResolutionOutcome,
  type ResolutionRunDetail,
  type ResolutionRunSummary,
} from '../lib/api.ts'
import { Button } from './Button.tsx'

/**
 * The executions of one case, and what any of them actually compared.
 *
 * A single score says little here. The resolver sets no temperature, and the
 * same case has answered `unresolved` on one run and `consistent` on the next
 * with nothing changed, so one number cannot separate a regression from a
 * flap. Only the sequence can.
 *
 * Opening a run shows the two sides rather than the number, because the number
 * is never the actionable part: knowing a case scored 6/7 tells you nothing
 * about which ingredient, or whether we accused somebody who was right.
 */

const OUTCOME: Readonly<Record<ResolutionOutcome, string>> = {
  consistent: 'Concorda',
  suggest_correction: 'Sugere correção',
  unresolved: 'Não conclui',
}

const GRADE: Readonly<Record<string, string>> = {
  correct: 'igual',
  false_accusation: 'acusação falsa',
  wrong_rival: 'correção errada',
  missed_catch: 'deixou passar',
  other: 'diferente',
}

export function ResolutionRuns({ caseId, onClose }: { caseId: string; onClose: () => void }) {
  const [runs, setRuns] = useState<ResolutionRunSummary[] | undefined>()
  const [open, setOpen] = useState<ResolutionRunDetail | undefined>()
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    void api
      .resolutionRuns(caseId)
      .then((r) => setRuns(r.runs))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [caseId])

  async function load(runId: string) {
    setError(undefined)
    try {
      const { run } = await api.resolutionRun(caseId, runId)
      setOpen(run)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao abrir')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Execuções deste caso"
      onClick={onClose}
    >
      <div
        className="rounded-base bg-background-raised border-stroke-weak flex max-h-[85vh] w-full max-w-[56rem] flex-col gap-3 overflow-y-auto border p-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-tPSemi text-text-strong">
            {open === undefined ? 'Execuções deste caso' : `Execução de ${when(open.created_at)}`}
          </h2>
          <div className="flex gap-2">
            {open !== undefined && (
              <Button
                variant="tertiary"
                tone="neutral"
                size="small"
                onClick={() => setOpen(undefined)}
              >
                Voltar
              </Button>
            )}
            <Button variant="tertiary" tone="neutral" size="small" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>

        {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

        {open !== undefined ? (
          <Comparison run={open} />
        ) : runs === undefined ? (
          <p className="text-tP text-text-weak">Carregando...</p>
        ) : runs.length === 0 ? (
          <p className="text-tP text-text-weak">
            Nenhuma execução ainda. Use <strong>Executar</strong> para rodar este caso contra o
            resolvedor de verdade.
          </p>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Quando', 'Quem', 'Acertos', 'Acusações falsas', 'Deixou passar'].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-2 text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.id}
                  tabIndex={0}
                  role="button"
                  className="hover:bg-fill-weak/40 cursor-pointer"
                  onClick={() => void load(run.id)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    void load(run.id)
                  }}
                >
                  <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-2">
                    {when(run.created_at)}
                  </td>
                  <td className="border-stroke-weak text-tSmall text-text-weak border-b px-3 py-2">
                    {run.ran_by}
                  </td>
                  <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-2 tabular-nums">
                    {run.score.correct}/{run.score.total}
                  </td>
                  {/*
                    Red whenever it is not zero. A run that agreed everywhere
                    and accused once is worse than one that quietly missed a
                    mis-pick, however the totals land.
                  */}
                  <td
                    className={`border-stroke-weak text-tPSemi border-b px-3 py-2 tabular-nums ${
                      run.score.falseAccusations > 0 ? 'text-text-error' : 'text-text-weak'
                    }`}
                  >
                    {run.score.falseAccusations}
                  </td>
                  <td
                    className={`border-stroke-weak text-tP border-b px-3 py-2 tabular-nums ${
                      run.score.missedCatches > 0 ? 'text-text-warning' : 'text-text-weak'
                    }`}
                  >
                    {run.score.missedCatches}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

/**
 * The two sides of one execution.
 *
 * Side by side rather than a diff of prose: the units being compared are
 * outcomes, and an outcome either is the expected one or is not. What varies
 * is *how* it is not, which is what the grade names.
 *
 * The expectations come from the run, not from the case as it reads now. A
 * case is editable, and grading an old run against today's answers would let
 * the history quietly rewrite itself.
 */
function Comparison({ run }: { run: ResolutionRunDetail }) {
  const observed = new Map(run.observed.map((o) => [`${o.prescribed} ${o.selected}`, o]))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-4">
        <Metric label="Acertos" value={`${run.score.correct}/${run.score.total}`} />
        <Metric
          label="Acusações falsas"
          value={String(run.score.falseAccusations)}
          tone={run.score.falseAccusations > 0 ? 'error' : 'ok'}
        />
        <Metric
          label="Deixou passar"
          value={String(run.score.missedCatches)}
          tone={run.score.missedCatches > 0 ? 'warn' : 'ok'}
        />
        <Metric
          label="Correção errada"
          value={String(run.score.wrongRivals)}
          tone={run.score.wrongRivals > 0 ? 'warn' : 'ok'}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Side title="Gabarito">
          {run.expected.map((ingredient, index) => (
            <Row
              key={`e-${ingredient.prescribed}-${ingredient.selected}-${index}`}
              term={ingredient.prescribed}
              selected={ingredient.selected}
              outcome={OUTCOME[ingredient.expect]}
              rival={ingredient.expectRival}
            />
          ))}
        </Side>

        <Side title="Execução">
          {run.expected.map((ingredient, index) => {
            const actual = observed.get(`${ingredient.prescribed} ${ingredient.selected}`)
            const row = run.score.rows.find(
              (r) => r.prescribed === ingredient.prescribed && r.selected === ingredient.selected,
            )
            return (
              <Row
                key={`o-${ingredient.prescribed}-${ingredient.selected}-${index}`}
                term={ingredient.prescribed}
                selected={ingredient.selected}
                outcome={actual === undefined ? '--' : OUTCOME[actual.outcome]}
                rival={actual?.rivalCode ?? null}
                note={actual?.reason}
                grade={row?.grade ?? 'other'}
              />
            )
          })}
        </Side>
      </div>
    </div>
  )
}

function Side({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-base border-stroke-weak flex min-w-0 flex-col border">
      <div className="bg-background-sunken border-stroke-weak text-tPSemi text-text-strong border-b px-3 py-2">
        {title}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  )
}

function Row({
  term,
  selected,
  outcome,
  rival,
  note,
  grade,
}: {
  term: string
  selected: string
  outcome: string
  rival: string | null | undefined
  note?: string | undefined
  grade?: string | undefined
}) {
  /*
   * Coloured only where the two sides disagree, and only on the side that
   * reports what happened. Painting the answer key would suggest the answer
   * key is the thing that is wrong.
   */
  const tone =
    grade === undefined || grade === 'correct'
      ? ''
      : grade === 'false_accusation'
        ? 'bg-fill-error-weak/40'
        : 'bg-fill-warning-weak/40'

  return (
    <div
      className={`border-stroke-weak flex flex-col gap-0.5 border-b px-3 py-2 last:border-b-0 ${tone}`}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-tP text-text-strong">{term}</span>
        <span className="text-tTiny text-text-weak font-mono">{selected}</span>
      </div>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-tSmall text-text-weak">{outcome}</span>
        {rival !== null && rival !== undefined && (
          <span className="text-tTiny text-text-weak font-mono">{`-> ${rival}`}</span>
        )}
        {grade !== undefined && grade !== 'correct' && (
          <span className="text-tTiny text-text-strong">{GRADE[grade] ?? grade}</span>
        )}
      </div>
      {note !== undefined && <span className="text-tTiny text-text-weak">{note}</span>}
    </div>
  )
}

function Metric({
  label,
  value,
  tone = 'ok',
}: {
  label: string
  value: string
  tone?: 'ok' | 'warn' | 'error'
}) {
  const colour =
    tone === 'error'
      ? 'text-text-error'
      : tone === 'warn'
        ? 'text-text-warning'
        : 'text-text-strong'
  return (
    <span className="flex items-baseline gap-1">
      <span className="text-tTiny text-text-weak uppercase">{label}</span>
      <span className={`text-tPSemi tabular-nums ${colour}`}>{value}</span>
    </span>
  )
}

/** Local time: the reader is asking "was that before or after my change?" */
function when(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR')
}
